import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert } from '../../components/ui/alert';
import { Paper, Box, Stack, Grid, Title as MantineTitle, Text, Group, NumberInput, Button } from '@mantine/core';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CitationLink } from '../../components/ui/citation';
import { IconMapping, citations as allCitations, historicalPriceData, PROPERTY_AREA_SQM, initialProjectedUnitPriceDefault, initialProjectedMonthlyRentDefault, initialAgentPriceDefault, initialActualMonthlyRentDefault, commercialPropertyMarketData, initialTaxRateDefault } from '../../data';

interface OverviewPanelProps {
  editableProjectedUnitPrice: number | '';
  setEditableProjectedUnitPrice: (value: number | '') => void;
  editableProjectedMonthlyRent: number | '';
  setEditableProjectedMonthlyRent: (value: number | '') => void;
  editableAgentPrice: number | '';
  setEditableAgentPrice: (value: number | '') => void;
  editableActualMonthlyRent: number | '';
  setEditableActualMonthlyRent: (value: number | '') => void;
  editableTaxRate: number | '';
  setEditableTaxRate: (value: number | '') => void;
}

const OverviewPanel: React.FC<OverviewPanelProps> = ({
  editableProjectedUnitPrice,
  setEditableProjectedUnitPrice,
  editableProjectedMonthlyRent,
  setEditableProjectedMonthlyRent,
  editableAgentPrice,
  setEditableAgentPrice,
  editableActualMonthlyRent,
  setEditableActualMonthlyRent,
  editableTaxRate,
  setEditableTaxRate
}) => {
  const agentPriceFull = Number(editableAgentPrice) * 1000000;
  const actualAnnualRent = Number(editableActualMonthlyRent) * 12;
  const taxRateDecimal = Number(editableTaxRate);

  const netProceedsAfterTaxFromAgentPrice = agentPriceFull * (1 - taxRateDecimal);
  const yieldOnNetProceedsFromAgentPrice = netProceedsAfterTaxFromAgentPrice > 0 && actualAnnualRent > 0
    ? (actualAnnualRent / netProceedsAfterTaxFromAgentPrice) * 100
    : 0;
  const paybackPeriodOnNetProceedsFromAgentPrice = actualAnnualRent > 0 && netProceedsAfterTaxFromAgentPrice > 0
    ? netProceedsAfterTaxFromAgentPrice / actualAnnualRent
    : Infinity;

  return (
    <Stack gap="lg">
      <Card>
        <CardHeader>
          <CardTitle>
            <Group gap="xs">
              <IconMapping.Building2 size={20} />
              物業概況
            </Group>
          </CardTitle>
        </CardHeader>
        <CardContent pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack>
                <MantineTitle order={4} mb="sm">物業資訊</MantineTitle>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text c="dimmed">物業類型</Text>
                    <Text fw={500}>小區內商舖</Text>
                  </Group>
                  <Group justify="space-between"><Text c="dimmed">建築面積</Text><Text fw={500}>{PROPERTY_AREA_SQM}平方米</Text></Group>
                  <Group justify="space-between"><Text c="dimmed">所在區域</Text><Text fw={500}>廣州市荔灣區芳村</Text></Group>
                  <Group justify="space-between"><Text c="dimmed">物業特點</Text><Text fw={500}>小區內，非臨街，非地鐵附近</Text></Group>
                  <Group justify="space-between"><Text c="dimmed">目前用途</Text><Text fw={500}>出租中</Text></Group>
                  <Group align="flex-end" justify="space-between">
                    <NumberInput
                      label="目前月租金 (2025年預計 元/月)"
                      value={editableProjectedMonthlyRent}
                      onChange={(value) => setEditableProjectedMonthlyRent(value === '' ? '' : Number(value))}
                      min={0}
                      step={50}
                      styles={{ label: { marginRight: 'auto' } }}
                      rightSection={<Text size="sm" c="dimmed">元/月</Text>}
                    />
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%', marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
                    預設值為2025年預測租金: {initialProjectedMonthlyRentDefault.toLocaleString()}元/月
                  </Text>
                </Stack>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack>
                <MantineTitle order={4} mb="sm">市場估值 (基於下方輸入調整)</MantineTitle>
                <Stack gap="xs">
                  <Group align="flex-end" justify="space-between">
                    <NumberInput
                      label="目前單價 (元/平方米)"
                      value={editableProjectedUnitPrice}
                      onChange={(value) => setEditableProjectedUnitPrice(value === '' ? '' : Number(value))}
                      min={0}
                      step={100}
                      styles={{ label: { marginRight: 'auto' } }}
                      rightSection={<Text size="sm" c="dimmed">元/平方米</Text>}
                    />
                  </Group>
                  <Group justify="space-between">
                    <Text c="dimmed">總價估值 (預計)</Text>
                    <Text fw={500}>
                      {editableProjectedUnitPrice && PROPERTY_AREA_SQM
                        ? (Number(editableProjectedUnitPrice) * PROPERTY_AREA_SQM).toLocaleString()
                        : 'N/A'}元
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>調整上方單價 × {PROPERTY_AREA_SQM}平方米</Text>
                  <Group justify="space-between">
                    <Text c="dimmed">年租金收入 (預計)</Text>
                    <Text fw={500}>
                      {editableProjectedMonthlyRent
                        ? (Number(editableProjectedMonthlyRent) * 12).toLocaleString()
                        : 'N/A'}元
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>調整上方月租金 × 12</Text>
                  <Group justify="space-between">
                    <Text c="dimmed">租金回報率 (預計)</Text>
                    <Text fw={500}>
                      {(editableProjectedMonthlyRent && editableProjectedUnitPrice && Number(editableProjectedUnitPrice) > 0)
                        ? ((Number(editableProjectedMonthlyRent) * 12) / (Number(editableProjectedUnitPrice) * PROPERTY_AREA_SQM) * 100).toFixed(2)
                        : 'N/A'}%
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>(年租金收入 ÷ 總價估值) × 100</Text>
                  <Group justify="space-between">
                    <Text c="dimmed">回本年期 (預計)</Text>
                    <Text fw={500}>
                      {(editableProjectedMonthlyRent && editableProjectedUnitPrice && Number(editableProjectedMonthlyRent) > 0)
                        ? ((Number(editableProjectedUnitPrice) * PROPERTY_AREA_SQM) / (Number(editableProjectedMonthlyRent) * 12)).toFixed(1)
                        : 'N/A'}年
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>總價估值 ÷ 年租金收入</Text>

                  <MantineTitle order={5} mt="sm" mb="xs">基於中介推薦價及實際租金 (可調整)</MantineTitle>
                  <Group align="flex-end" justify="space-between">
                    <NumberInput
                      label="中介推薦總價 (百萬元)"
                      value={editableAgentPrice}
                      onChange={(value) => setEditableAgentPrice(value === '' ? '' : Number(value))}
                      min={0}
                      step={0.01}
                      decimalScale={2}
                      styles={{ label: { marginRight: 'auto' } }}
                      rightSection={<Text size="sm" c="dimmed">百萬元</Text>}
                    />
                  </Group>
                   <Group align="flex-end" justify="space-between">
                    <NumberInput
                      label="實際月租金 (2024年 元/月)"
                      value={editableActualMonthlyRent}
                      onChange={(value) => setEditableActualMonthlyRent(value === '' ? '' : Number(value))}
                      min={0}
                      step={50}
                      styles={{ label: { marginRight: 'auto' } }}
                      rightSection={<Text size="sm" c="dimmed">元/月</Text>}
                    />
                  </Group>
                  <Group justify="space-between">
                    <Text c="dimmed">年租金收入 (2024年實際)</Text>
                    <Text fw={500}>
                      {editableActualMonthlyRent
                        ? (Number(editableActualMonthlyRent) * 12).toLocaleString()
                        : 'N/A'}元
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>調整上方實際月租金 × 12</Text>
                  <Group justify="space-between">
                    <Text c="dimmed">租金回報率</Text>
                    <Text fw={500}>
                      {(editableActualMonthlyRent && editableAgentPrice && Number(editableAgentPrice) > 0)
                        ? ((Number(editableActualMonthlyRent) * 12) / (Number(editableAgentPrice) * 1000000) * 100).toFixed(2)
                        : 'N/A'}%
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>(年租金收入 ÷ 中介推薦總價) × 100</Text>
                  <Group justify="space-between">
                    <Text c="dimmed">回本年期</Text>
                    <Text fw={500}>
                      {(editableActualMonthlyRent && editableAgentPrice && Number(editableActualMonthlyRent) > 0)
                        ? ((Number(editableAgentPrice) * 1000000) / (Number(editableActualMonthlyRent) * 12)).toFixed(1)
                        : 'N/A'}年
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>中介推薦總價 ÷ 年租金收入</Text>
                  <Text size="xs" c="dimmed" mt="xs">註: 此處回報率与回本年期基於上方中介推薦價和實際月租金計算。</Text>

                  <MantineTitle order={6} mt="sm" mb="xs">考慮稅務影響 (基於上方中介推薦價、實際租金及下方稅率)</MantineTitle>
                  <Group align="flex-end" justify="space-between">
                    <NumberInput
                      label="預計綜合稅率"
                      value={editableTaxRate}
                      onChange={(value) => setEditableTaxRate(value === '' ? '' : Number(value))}
                      min={0}
                      max={1}
                      step={0.01}
                      decimalScale={2}
                      styles={{ label: { marginRight: 'auto' } }}
                      rightSection={<Text size="sm" c="dimmed">例: 0.5 = 50%</Text>}
                    />
                  </Group>
                  <Group justify="space-between">
                    <Text c="dimmed">稅後實際可回收資金</Text>
                    <Text fw={500}>
                      {(editableAgentPrice && editableTaxRate !== '' && !isNaN(netProceedsAfterTaxFromAgentPrice))
                        ? netProceedsAfterTaxFromAgentPrice.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})
                        : 'N/A'}元
                    </Text>
                  </Group>
                   <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>中介推薦總價 × (1 - 稅率)</Text>
                  <Group justify="space-between">
                    <Text c="dimmed">基於稅後資金的租金回報率</Text>
                    <Text fw={500}>
                      {(editableActualMonthlyRent && editableAgentPrice && editableTaxRate !== '' && !isNaN(yieldOnNetProceedsFromAgentPrice))
                        ? yieldOnNetProceedsFromAgentPrice.toFixed(2)
                        : 'N/A'}%
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>(年租金收入 ÷ 稅後可回收資金) × 100</Text>
                  <Group justify="space-between">
                    <Text c="dimmed">基於稅後資金的回本年期</Text>
                    <Text fw={500}>
                      {(editableActualMonthlyRent && editableAgentPrice && editableTaxRate !== '' && !isNaN(paybackPeriodOnNetProceedsFromAgentPrice))
                        ? (paybackPeriodOnNetProceedsFromAgentPrice === Infinity ? 'N/A (無租金或無回收資金)' : paybackPeriodOnNetProceedsFromAgentPrice.toFixed(1))
                        : 'N/A'}年
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ textAlign: 'right', width: '100%' }}>稅後可回收資金 ÷ 年租金收入</Text>
                  <Text size="xs" c="dimmed" mt="xs">註: 此處回報率与回本年期基於上方中介推薦價和實際月租金計算。</Text>
                </Stack>
              </Stack>
            </Grid.Col>
          </Grid>
          <Group justify="center" mt="md">
            <Button
                leftSection={<IconMapping.RotateCcw size={16} />}
                onClick={() => {
                    setEditableProjectedUnitPrice(initialProjectedUnitPriceDefault);
                    setEditableProjectedMonthlyRent(initialProjectedMonthlyRentDefault);
                    setEditableAgentPrice(initialAgentPriceDefault);
                    setEditableActualMonthlyRent(initialActualMonthlyRentDefault);
                    setEditableTaxRate(initialTaxRateDefault);
                }}
                variant="light"
            >
                重設為預設值
            </Button>
          </Group>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <Group gap="xs">
              <IconMapping.MapPin size={20}/>
              區域概況
            </Group>
          </CardTitle>
        </CardHeader>
        <CardContent pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6}}>
              <MantineTitle order={4} mb="sm">芳村區域特點</MantineTitle>
              <Text mb="sm">
                芳村位於廣州市荔灣區西部，是廣州市傳統的商貿區域之一。根據廣州市統計局數據，芳村區擁有個體經營戶12,329戶，營業收入43.63億元，經營面積74.3萬平方米<CitationLink id="4" callType="quote" citations={allCitations}/>。區域內商業氛圍濃厚，但近年來受到周邊新興商圈的衝擊，商業活力有所下降。
              </Text>
              <Text>
                芳村區域內小區商舖主要服務於周邊居民的日常生活需求，包括餐飲、零售、生活服務等業態。小區附近有大型肉菜市場，買菜還是比較方便，每天早上人流量較大，附近還有錢大媽、番薯藤等買菜的地方，以及一些早餐店<CitationLink id="5" callType="quote" citations={allCitations}/>。
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6}}>
              <MantineTitle order={4} mb="sm">區域商業發展趨勢</MantineTitle>
              <Stack gap="xs">
                <Group gap="xs"><IconMapping.ArrowRight size={20} color="green" /><Text>傳統商貿區域，商業基礎設施相對完善</Text></Group>
                <Group gap="xs"><IconMapping.ArrowRight size={20} color="green" /><Text>區域內居民消費能力穩定，但增長緩慢</Text></Group>
                <Group gap="xs"><IconMapping.ArrowDown size={20} color="red" /><Text>受到周邊新興商圈競爭，商業吸引力有所下降</Text></Group>
                <Group gap="xs"><IconMapping.ArrowDown size={20} color="red" /><Text>電商衝擊傳統零售業，小型商舖經營壓力增大</Text></Group>
                <Group gap="xs"><IconMapping.ArrowUp size={20} color="blue" /><Text>社區商業服務需求穩定，特別是餐飲和生活服務業</Text></Group>
              </Stack>
            </Grid.Col>
          </Grid>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle><Group gap="xs"><IconMapping.BarChart3 size={20} />市場概覽</Group></CardTitle>
        </CardHeader>
        <CardContent pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
               <MantineTitle order={4} mb="sm">芳村商業地產市場現狀</MantineTitle>
               <Text mb="sm">
                 根據安居客數據，芳村2024年12月二手房均價為31,512元/平方米<CitationLink id="1" callType="quote" citations={allCitations}/>。作為對比，2023年12月芳村二手房均價為31,806元/平方米<CitationLink id="6" callType="quote" citations={allCitations}/>，同比下跌約0.92%。新房方面，2025年2月芳村新房樓盤平均單價為56,480.40元/平方米<CitationLink id="2" callType="quote" citations={allCitations}/>。
               </Text>
               <Text mb="sm">
                 廣州商舖市場在2020年供應48.7萬平方米，成交50.7萬平方米<CitationLink id="7" callType="quote" citations={allCitations}/>。2021年供應約40.3萬平方米，成交約40.5萬平方米<CitationLink id="8" callType="quote" citations={allCitations}/>。
                 2022年，廣州零售物業市場新增供應105萬平方米，淨吸納量為60萬平方米<CitationLink id="9" callType="quote" citations={allCitations}/>。
                 到了2023年，新增供應約65.7萬平方米，淨吸納量約55.6萬平方米<CitationLink id="10" callType="quote" citations={allCitations}/>。
                 預計2024年市場將新增約70萬平方米供應<CitationLink id="11" callType="quote" citations={allCitations}/>，但具體成交量數據暫缺。
               </Text>
               <Text>
                 整體來看，商舖及零售物業市場在經歷2020年的波動後，供應量和需求（淨吸納量）在後續幾年有所變化，市場呈現出一定的調整和恢復態勢。投資者需關注不同類型商業物業（商舖 vs 零售物業）的指標差異以及未來市場供應對租金和空置率的潛在影響。
               </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <MantineTitle order={4} mb="sm">廣州商舖/零售物業市場供求 (2020-2024)</MantineTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={commercialPropertyMarketData}
                  margin={{
                    top: 5, right: 30, left: 20, bottom: 5,
                  }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis label={{ value: '供應量/成交量 (萬平方米)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="supply" fill="#8884d8" name="供應量" />
                  <Bar dataKey="transaction" fill="#82ca9d" name="成交量/淨吸納量" />
                </BarChart>
              </ResponsiveContainer>
               <Text size="xs" c="dimmed" mt="xs">
                數據來源: <CitationLink id="7" callType="quote" citations={allCitations}/>, <CitationLink id="8" callType="quote" citations={allCitations}/>, <CitationLink id="9" callType="quote" citations={allCitations}/>, <CitationLink id="10" callType="quote" citations={allCitations}/>, <CitationLink id="11" callType="quote" citations={allCitations}/>. 
                2022-2024年數據為零售物業，2020-2021年為商舖。2024年成交量數據 N/A。
              </Text>
            </Grid.Col>
          </Grid>
        </CardContent>
      </Card>

      <Alert 
        title={<MantineTitle order={5}>報告摘要</MantineTitle>} 
        icon={<IconMapping.AlertCircle size={16} />} 
        color="yellow"
      >
        <Text>
        本報告分析了廣州市荔灣區芳村過去15年商業地產價格走勢，重點關注37.5平方米小區內商舖的投資價值。根據市場數據分析，該商舖目前市場估值約為139.5萬元，略高於中介推薦的130萬元售價。目前月租金2,100元處於市場合理水平，但租金回報率僅為1.94%，低於銀行存款利率。考慮到芳村區域商業發展趨勢和物業特點，建議業主考慮在適當時機出售物業，以規避未來可能的市場風險。
        </Text>
      </Alert>
    </Stack>
  );
};

export default OverviewPanel; 